import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CandidatePanel } from "@/components/hte/CandidatePanel";
import { EMPTY_PROGRAM, ProgramForm } from "@/components/program/ProgramForm";
import { OfficialTemplates, TemplateList } from "@/components/program/Templates";
import { errMsg, isCancel, runJob, useApi } from "@/lib/api";
import { useMe } from "@/lib/me";
import { aarSummary } from "@/lib/program";

const toRef = (c) => ({ surah: c.ayat_ref.surah, from: c.ayat_ref.from, to: c.ayat_ref.to });

export default function ProgramPage() {
  const api = useApi();
  const me = useMe();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const dari = params.get("dari");
  const [form, setForm] = useState(EMPTY_PROGRAM);
  const [parent, setParent] = useState(null);
  const [cards, setCards] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const ctrl = useRef(null);

  useEffect(() => {
    if (!dari) { setParent(null); setForm(EMPTY_PROGRAM); return; }
    api("get", `/kajian/${dari}`).then((k) => {
      if (k.jenis !== "program") return;
      setParent(k);
      const p = k.program;
      const r = k.ayat_ref;
      const ayat = r ? `${r.surah}:${r.from}${r.to && r.to !== r.from ? `-${r.to}` : ""}` : (p.ayat || "");
      setForm({ tajuk: p.tajuk, peserta: params.get("jenis") === "peserta" ? "" : p.peserta, tempoh: p.tempoh, cabaran: p.cabaran,
        objektif: p.objektif, ayat, penambahbaikan: aarSummary(k.aar, k.maklum_balas) });
    }).catch((e) => setError(errMsg(e)));
  }, [api, dari]); // eslint-disable-line react-hooks/exhaustive-deps

  const begin = () => { ctrl.current = new AbortController(); setLoading(true); setError(""); return ctrl.current.signal; };
  const end = () => { setLoading(false); setStatus(""); };
  const cancel = () => { ctrl.current?.abort(); end(); toast("Dihentikan"); };
  const program = () => Object.fromEntries(Object.entries(form).map(([k, v]) => [k, (v || "").trim()]));

  const submit = async () => {
    const signal = begin();
    try {
      if (form.ayat.trim()) {
        setStatus("Mengesahkan rujukan ayat…");
        const r = await api("post", "/hte/user-refs", { refs: form.ayat });
        if (!r.calon.length) throw new Error(r.gagal[0]?.error || "Rujukan ayat tidak dapat dibaca.");
        end(); setConfirm(r.calon[0]); return;
      }
      setStatus("Mencari calon ayat yang sesuai (percuma)…");
      const r = await runJob(api, "/hte/candidates", { isu: `${form.tajuk} — ${form.cabaran}`.slice(0, 1500), konteks: "Pendidikan dan latihan", exclude: [] }, signal);
      setCards(r.calon);
    } catch (e) { if (!isCancel(e)) setError(e.message && !e.response ? e.message : errMsg(e)); } finally { end(); }
  };

  const build = async (c) => {
    setConfirm(null);
    const signal = begin();
    setStatus("Membaca tafsir dan membina kerangka program… (1–2 minit)");
    try {
      const k = await runJob(api, "/hte/generate", { ...toRef(c), mod: "bumi", konteks: "Pendidikan dan latihan", sendiri: c.sumber !== "ai", kenapa: c.kenapa,
        rujukan: c.rujukan, parent_kajian_id: parent?.id || null, program: program() }, signal);
      me?.refresh();
      toast.success("Kerangka program disimpan dalam Ruang Kerja");
      nav(`/kajian/${k.id}`);
    } catch (e) { if (!isCancel(e)) setError(errMsg(e)); } finally { end(); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-14 space-y-8">
      <header className="fade-up">
        <div className="eyebrow">Sebelum Program</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink mt-3">Saya Ada Program</h1>
        <p className="text-base text-mute mt-4 max-w-2xl">Isi maklumat program. HTE akan mencadangkan asas wahyu, analisis Q → V → K → I, objektif, kandungan, aktiviti dan soalan refleksi.</p>
      </header>
      {!dari && <><OfficialTemplates /><TemplateList /></>}
      <ProgramForm form={form} setForm={setForm} loading={loading} status={status} onSubmit={submit} onCancel={cancel} parentTitle={parent?.program?.tajuk} />
      {error && <div className="rounded-xl p-4 bg-warn-soft text-warn text-sm flex gap-2" role="alert" data-testid="program-error"><AlertTriangle size={16} className="shrink-0 mt-0.5" /><span>{error}{error.includes("Kuota carian") && <> <Link to="/akaun" className="underline font-semibold">Pergi ke Akaun</Link></>}</span></div>}
      {cards.length > 0 && <CandidatePanel cards={cards} selectedId={null} onPick={setConfirm} onMore={() => {}} onOwn={() => {}} busy={loading} ownFailed={[]} picking={loading} hideMore />}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="program-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Bina kerangka program?</AlertDialogTitle>
            <AlertDialogDescription>Asas wahyu: <strong>{confirm?.rujukan}</strong>. Tindakan ini menggunakan <strong>1 kuota carian</strong>{me?.akaun && me.role !== "superadmin" ? ` (baki anda: ${me.akaun.jumlah_baki})` : ""}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="program-confirm-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={() => build(confirm)} data-testid="program-confirm-build">Bina (1 kuota)</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
